import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import api from '../api/client';
import { Buildings, Plus, PencilSimple, Trash, FloppyDisk, X } from '@phosphor-icons/react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

function Services() {
  const [services, setServices] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [sRes, bRes] = await Promise.all([
        api.get('/services', { params: { service_id: 'all' } }),
        api.get('/batches', { params: { service_id: 'all' } }),
      ]);
      setServices(sRes.data);
      setBatches(bRes.data);
    } catch (error) {
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const batchCount = (serviceId) => batches.filter((b) => b.service_id === serviceId).length;

  const createService = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    try {
      await api.post('/services', { name: newName.trim() });
      toast.success('Service created');
      setNewName('');
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create service');
    }
  };

  const saveEdit = async (id) => {
    if (!editName.trim()) return;
    try {
      await api.put(`/services/${id}`, { name: editName.trim() });
      toast.success('Service renamed');
      setEditingId(null);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to rename');
    }
  };

  const deleteService = async (svc) => {
    if (!window.confirm(`Delete service "${svc.name}"?`)) return;
    try {
      await api.delete(`/services/${svc.id}`);
      toast.success('Service deleted');
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <Layout>
      <div className="px-6 sm:px-8 lg:px-12 py-8" data-testid="services-page">
        <div className="flex items-center gap-3 mb-2">
          <Buildings size={28} weight="duotone" className="text-blue-400" />
          <h1 className="text-3xl font-black tracking-tight text-white">Services</h1>
        </div>
        <p className="text-sm text-slate-400 mb-8">Each service (academy) groups its own batches, students and reports. Assign batches to a service from the Batches page.</p>

        <form onSubmit={createService} className="flex gap-3 mb-8 max-w-md" data-testid="create-service-form">
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="New service name (e.g. Yoga Studio)"
            className="bg-slate-900 border-slate-700 text-white"
            data-testid="new-service-input"
          />
          <Button type="submit" data-testid="create-service-btn" className="bg-blue-600 hover:bg-blue-500 text-white shrink-0">
            <Plus size={16} weight="bold" className="mr-1.5" /> Add
          </Button>
        </form>

        {loading ? (
          <p className="text-slate-400">Loading...</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="services-list">
            {services.map((svc) => (
              <div key={svc.id} data-testid={`service-${svc.id}`} className="bg-slate-900 border border-slate-800 rounded-xl p-5">
                {editingId === svc.id ? (
                  <div className="flex items-center gap-2">
                    <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="bg-slate-950 border-slate-700 text-white h-9" data-testid={`edit-service-input-${svc.id}`} autoFocus />
                    <Button size="sm" onClick={() => saveEdit(svc.id)} data-testid={`save-service-${svc.id}`} className="h-9 px-2 bg-green-600 hover:bg-green-500 text-white"><FloppyDisk size={16} /></Button>
                    <Button size="sm" variant="outline" onClick={() => setEditingId(null)} className="h-9 px-2 border-slate-700 text-slate-300"><X size={16} /></Button>
                  </div>
                ) : (
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-lg font-bold text-white">{svc.name}</h3>
                      <p className="text-xs text-slate-400 mt-1">{batchCount(svc.id)} batch(es)</p>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => { setEditingId(svc.id); setEditName(svc.name); }} data-testid={`edit-service-${svc.id}`} className="p-1.5 rounded-md text-blue-400 hover:bg-slate-800" title="Rename">
                        <PencilSimple size={16} weight="duotone" />
                      </button>
                      <button onClick={() => deleteService(svc)} data-testid={`delete-service-${svc.id}`} className="p-1.5 rounded-md text-rose-400 hover:bg-slate-800" title="Delete">
                        <Trash size={16} weight="duotone" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

export default Services;
